import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { Images } from '../../../assets/assets';
import StatCard from '../../Shared/StatCard/StatCard';
import patientService from '../../../services/patientService';
import scanService from '../../../services/scanService';
import authService from '../../../services/authService';

export default function Statistics() {
  const [stats, setStats] = useState([
    {
      title: "Total Patients",
      value: "0",
      subtitle: "Loading...",
      tone: "primary",
      Icon: Images.PatientsIcon,
    },
    {
      title: "Longitudinal Cases",
      value: "0",
      subtitle: "Loading...",
      tone: "purple",
      Icon: Images.DocsIcon,
    },
    {
      title: "High Risk",
      value: "0",
      subtitle: "Loading...",
      tone: "red",
      Icon: Images.AlertIcon,
    },
    {
      title: "Scans Processed Today",
      value: "0",
      subtitle: "Loading...",
      tone: "teal",
      Icon: Images.ScanIcon,
    },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  // High risk check simplified: based on EDSS threshold from latest scan
  const isHighRiskFromLatest = (latestScan) => {
    if (!latestScan || latestScan.edssScore === undefined || latestScan.edssScore === null) return false;
    return latestScan.edssScore >= 6;
  };

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const isAdmin = authService.isAdminAuthenticated();
      
      let totalPatients = 0;
      let longitudinalCases = 0;
      let scansToday = 0;
      let highRiskPatients = 0;

      // Fetch patients with latest scan data
      const patientsResponse = await patientService.getPatientsWithLatestScan({ 
        limit: 1000 
      });
      const patients = patientsResponse.data || [];
      totalPatients = patients.length;
      
      const today = new Date().toISOString().split('T')[0];
      const allScansToday = [];
      
      // Calculate longitudinal cases and high risk patients
      patients.forEach(patient => {
        const latestScan = patient.latestScan;
        // Patients with more than 1 scan are longitudinal cases
        if (patient.scanCount > 1) longitudinalCases++;
        // High risk if EDSS >= 6
        if (isHighRiskFromLatest(latestScan)) highRiskPatients += 1;
      });
      
      // Fetch all scans for each patient to count today's scans
      const patientScanPromises = patients.map(async (patient) => {
        const patientId = patient.id || patient._id;
        if (patientId) {
          try {
            const scansResp = await scanService.getScansByPatient(patientId);
            const patientScans = scansResp.data || [];
            const todayScans = patientScans.filter(scan => 
              scan.createdAt && scan.createdAt.split('T')[0] === today
            );
            return todayScans;
          } catch (error) {
            console.error(`Error fetching scans for patient ${patientId}:`, error);
            return [];
          }
        }
        return [];
      });
      
      // Wait for all scan fetches to complete
      const scansArrays = await Promise.all(patientScanPromises);
      scansArrays.forEach(todayScans => {
        allScansToday.push(...todayScans);
      });
      
      // Count all scans processed today across all patients
      scansToday = allScansToday.length;

      // Update statistics
      setStats([
        {
          title: "Total Patients",
          value: totalPatients.toString(),
          subtitle: isAdmin ? `All active patients` : `Your profile`,
          tone: "primary",
          Icon: Images.PatientsIcon,
        },
        {
          title: "Longitudinal Cases",
          value: longitudinalCases.toString(),
          subtitle: "Patients with multiple timepoints",
          tone: "purple",
          Icon: Images.DocsIcon,
        },
        {
          title: "High Risk",
          value: highRiskPatients.toString(),
          subtitle: "Required immediate attention",
          tone: "red",
          Icon: Images.AlertIcon,
        },
        {
          title: "Scans Processed Today",
          value: scansToday.toString(),
          subtitle: "Scheduled for analysis",
          tone: "teal",
          Icon: Images.ScanIcon,
        },
      ]);

    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      
      // Set error state
      setStats([
        {
          title: "Total Patients",
          value: "0",
          subtitle: "Error loading data",
          tone: "primary",
          Icon: Images.PatientsIcon,
        },
        {
          title: "Longitudinal Cases",
          value: "0",
          subtitle: "Error loading data",
          tone: "purple",
          Icon: Images.DocsIcon,
        },
        {
          title: "High Risk",
          value: "43",
          subtitle: "Required immediate attention",
          tone: "red",
          Icon: Images.AlertIcon,
        },
        {
          title: "Scans Processed Today",
          value: "0",
          subtitle: "Error loading data",
          tone: "teal",
          Icon: Images.ScanIcon,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row>
      {stats.map((stat, idx) => (
        <Col md={3} key={stat.title}>
          <StatCard {...stat} loading={loading} />
        </Col>
      ))}
    </Row>
  );
}